import { useMemo } from 'react';
import { T } from 'react-translator-component';
import {
    extraerDatosDeValues,
    validarPreciosDisponibles,
    getMonthName,
    obtenerPrecio,
    crearItem
} from './QuestionTotals.utils';

/**
 * Hook personalizado para calcular totales en upsellings
 * Maneja tanto cálculos mensuales como de categoría de habitación
 *
 * @param {Object} config - Configuración
 * @param {Array} config.values - Array de items seleccionados
 * @param {Object} config.settings - Configuración de upsellings
 * @param {boolean} config.useRoomCategory - Si usa categoría de habitación
 * @param {string} config.roomCategory - Código de categoría
 * @param {number} config.addonPrice - Precio del ítem/opción cuando no hay baremo (precio por día o unidad)
 * @param {string} config.checkoutDate - Fecha de checkout (YYYY-MM-DD)
 * @param {boolean} config.hideCheckoutDate - Si el selector oculta el día de checkout; no contar ese día en el total
 * @param {boolean} config.hideToday - Si el selector oculta hoy; no contar hoy en el total
 * @returns {{ items: Array, total: number, isInvalid: boolean, invalidCount: number }}
 */
export const useTotalCalculation = ({
    values,
    settings,
    useRoomCategory = false,
    roomCategory = null,
    addonPrice = null,
    checkoutDate = null,
    hideCheckoutDate = false,
    hideToday = false
}) => {
    return useMemo(() => {
        let items = [];
        let total = 0;
        let isInvalid = false;
        let invalidCount = 0;

        const settingsObj = typeof settings === 'string'
            ? (() => { try { return JSON.parse(settings); } catch { return null; } })()
            : settings;

        // Extraer datos de values
        const { selectedDays, hasMonthlyData, monthsData } = extraerDatosDeValues(values);
        // Días a cobrar: solo según hideCheckoutDate y hideToday (no inferir noches por último día)
        const rawDays = (selectedDays || []).map(d => String(d).slice(0, 10));
        let daysToCount = rawDays;
        if (hideCheckoutDate && checkoutDate) {
            const checkoutNorm = String(checkoutDate).slice(0, 10);
            daysToCount = daysToCount.filter(d => d !== checkoutNorm);
        }
        if (hideToday) {
            const today = new Date();
            const todayStr = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())).toISOString().slice(0, 10);
            daysToCount = daysToCount.filter(d => d !== todayStr);
        }
        const countSelectedDays = daysToCount.length;

        // Validar precios disponibles
        const validation = validarPreciosDisponibles(
            settingsObj,
            countSelectedDays,
            useRoomCategory,
            roomCategory,
            hasMonthlyData ? monthsData : null
        );

        if (!validation.isValid) {
            const hasPaxOrSelectQuestions = (settingsObj?.questions || []).some(q => q.type === 'pax-qty-selector' || q.type === 'select');
            // Excepción: solo fechas sin baremo y SIN preguntas de pasajeros → usar precio base del ítem (nunca un total previo)
            if (countSelectedDays > 0 && addonPrice != null && addonPrice > 0 && !hasMonthlyData && !useRoomCategory && !hasPaxOrSelectQuestions) {
                const rawUnit = Number(addonPrice);
                const unitPrice = Number.isFinite(rawUnit) && rawUnit > 0 && rawUnit < 1e12 ? rawUnit : 0;
                if (unitPrice > 0) {
                    const subtotal = countSelectedDays * unitPrice;
                    items.push(crearItem(T('general.days') || 'Días', countSelectedDays, unitPrice, subtotal));
                    return { items, total: subtotal, isInvalid: false, invalidCount: 0 };
                }
            }
            return {
                items: [],
                total: 0,
                isInvalid: true,
                invalidCount: validation.invalidCount
            };
        }

        // CASO 1: Datos mensuales
        if (hasMonthlyData) {
            for (const [month, daysInMonth] of Object.entries(monthsData)) {
                const monthKey = `month-${month}`;
                const pricesArray = settingsObj?.prices?.[monthKey];
                const priceIndex = daysInMonth - 1;
                const price = obtenerPrecio(pricesArray, priceIndex);
                const subtotal = price;

                total += subtotal;

                const monthName = getMonthName(month, T);
                items.push(crearItem(monthName, daysInMonth, price, subtotal));
            }

            return { items, total, isInvalid: false, invalidCount: 0 };
        }

        // CASO 2: Con categoría de habitación (item virtual)
        if (useRoomCategory && roomCategory && countSelectedDays > 0) {
            const virtualKey = roomCategory.toUpperCase();
            const pricesArray = settingsObj?.prices?.[virtualKey];
            const priceIndex = 0; //uso siempre el primer precio
            const price = obtenerPrecio(pricesArray, priceIndex);
            const subtotal = price * countSelectedDays;
            const unitPrice = price;
            total += subtotal;
            items.push(crearItem(virtualKey, 1, unitPrice, subtotal));

            return { items, total, isInvalid: false, invalidCount: 0 };
        }

        // CASO 3: Items normales (sin meses ni roomCategory) — fechas + selectores de pasajeros
        if (values && values.length > 0 && settingsObj?.prices && countSelectedDays > 0) {
            const paxQuestions = (settingsObj.questions || []).filter(q => (q.type === 'select' || q.type === 'pax-qty-selector') && q.key);
            const priceKeys = Object.keys(settingsObj.prices || {}).filter(k => k !== 'price' && k !== 'dates');
            const hasPaxSelectors = paxQuestions.length > 0 && priceKeys.some(k => paxQuestions.some(q => q.key === k));

            const passengersItem = values.find(v => v?.passengers && typeof v.passengers === 'object');

            values.forEach((valueItem) => {
                Object.keys(valueItem).forEach((key) => {
                    if (key === 'price' || key === 'dates' || key === 'total') {
                        return;
                    }

                    const selectedValue = valueItem[key];
                    const question = settingsObj.questions?.find(q => q.key === key);
                    let quantity = parseInt(selectedValue) || 0;
                    if (question?.values && Array.isArray(question.values) && question.values.length > 0) {
                        const strVal = String(selectedValue).trim();
                        if (!strVal || !question.values.some(v => String(v) === strVal)) {
                            quantity = 0;
                        }
                    }

                    // Para pax-qty-selector con cantidad > 0, solo sumar si tiene pasajeros asignados (Seleccionar pasajero)
                    if (quantity > 0 && question?.type === 'pax-qty-selector') {
                        const assignedCount = passengersItem?.passengers?.[key]?.length ?? 0;
                        if (assignedCount !== quantity) quantity = 0;
                    }

                    if (quantity > 0) {
                        const pricesArray = settingsObj.prices[key];
                        if (!pricesArray) return;
                        const priceIndex = countSelectedDays - 1;
                        const price = obtenerPrecio(pricesArray, priceIndex);
                        const subtotal = quantity * price;

                        total += subtotal;

                        items.push(
                            crearItem(question?.label || key, quantity, price, subtotal)
                        );
                    }
                });
            });

            // Mostrar total solo si al menos una categoría aporta (no exigir todas)
            if (hasPaxSelectors && items.length === 0) {
                return { items: [], total: 0, isInvalid: true, invalidCount: 0, invalidReason: 'select-passengers' };
            }
        }

        // CASO 5: Solo fechas (date-selector) sin baremo por pasajeros: precio por día × días
        // No aplicar si hay preguntas pax/select (el total lo lleva CASO 3 cuando todas tienen valor)
        const hasPaxOrSelectQuestions = (settingsObj?.questions || []).some(q => q.type === 'pax-qty-selector' || q.type === 'select');
        if (countSelectedDays > 0 && total === 0 && !hasMonthlyData && !useRoomCategory && !hasPaxOrSelectQuestions) {
            let unitPrice = 0;
            let label = T('general.days') || 'Días';
            const priceKeys = settingsObj?.prices && Object.keys(settingsObj.prices).filter(k => k !== 'price' && k !== 'dates');
            const firstKey = priceKeys?.[0];
            if (firstKey) {
                const pricesArray = settingsObj.prices[firstKey];
                unitPrice = obtenerPrecio(pricesArray, 0);
                const question = settingsObj.questions?.find(q => q.key === firstKey);
                label = question?.label || firstKey;
            } else if (addonPrice != null && addonPrice > 0) {
                unitPrice = Number(addonPrice);
            }
            if (unitPrice > 0) {
                const subtotal = countSelectedDays * unitPrice;
                total += subtotal;
                items.push(crearItem(label, countSelectedDays, unitPrice, subtotal));
            }
        }
        // CASO 4: Solo cantidad (sin fechas): p. ej. "Number of Dogs" con precio por unidad
        // No aplicar si hay date-selector: en ese caso el total solo se muestra con fechas + pax (CASO 3)
        const hasDateSelectorQuestion = (settingsObj?.questions || []).some(q => q.type === 'date-selector');
        if (values && values.length > 0 && settingsObj?.prices && countSelectedDays === 0 && !hasDateSelectorQuestion) {
            const qtyQuestionKeys = (settingsObj.questions || [])
                .filter(q => (q.type === 'select' || q.type === 'pax-qty-selector') && q.key)
                .map(q => q.key);
            values.forEach((valueItem) => {
                Object.keys(valueItem).forEach((key) => {
                    if (key === 'price' || key === 'dates' || key === 'total') return;
                    if (!qtyQuestionKeys.includes(key)) return;

                    const selectedValue = valueItem[key];
                    const quantity = parseInt(selectedValue) || 0;
                    if (quantity > 0) {
                        const pricesArray = settingsObj.prices[key];
                        const price = obtenerPrecio(pricesArray, 0); // índice 0 = precio por unidad
                        const subtotal = quantity * price;
                        total += subtotal;
                        const question = settingsObj.questions?.find(q => q.key === key);
                        items.push(
                            crearItem(question?.label || key, quantity, price, subtotal)
                        );
                    }
                });
            });
        }

        const safeTotal = Number.isFinite(total) && total <= Number.MAX_SAFE_INTEGER ? total : 0;
        const safeItems = safeTotal !== total ? [] : items;
        return { items: safeItems, total: safeTotal, isInvalid: safeTotal !== total, invalidCount: safeTotal !== total ? 1 : 0 };
    }, [values, settings, useRoomCategory, roomCategory, addonPrice, checkoutDate, hideCheckoutDate, hideToday]);
};

